/**
 * External Funnel API (v1) client functions
 */

import type {
  FunnelRequestParams,
  FunnelResponse,
  FunnelSuggestionsResponse,
} from "@/lib/types/funnel-api";

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;

const EXTERNAL_API_URL =
  PUBLIC_IS_PROD === "TRUE"
    ? `https://app.humblytics.com/api/external/v1/`
    : PUBLIC_IS_LOCAL === "TRUE"
    ? `http://localhost:3000/api/external/v1/`
    : `https://humblytics.onrender.com/api/external/v1/`;

/**
 * Carries the HTTP status so callers can tell a plan limit (402) apart from a
 * genuine failure when reporting to Sentry.
 */
const createFunnelApiError = (
  label: string,
  response: Response,
  errorData: any,
): Error & { status: number } => {
  const error = new Error(
    errorData?.error?.message ||
      `${label} API error: ${response.status} ${response.statusText}`,
  ) as Error & { status: number };
  error.status = response.status;
  return error;
};

/**
 * Get funnel analysis data (sequential or unbounded)
 */
export const getFunnelAnalysis = async (
  propertyId: string,
  params: FunnelRequestParams,
  apiKey: string
): Promise<FunnelResponse> => {
  const queryParams = new URLSearchParams({
    start: params.start,
    end: params.end,
    steps: JSON.stringify(params.steps),
  });

  if (params.mode) {
    queryParams.append("mode", params.mode);
  }
  if (params.timezone) {
    queryParams.append("timezone", params.timezone);
  }
  if (params.granularity) {
    queryParams.append("granularity", params.granularity);
  }
  if (params.breakdown_by) {
    queryParams.append("breakdown_by", params.breakdown_by);
  }
  if (params.limit) {
    queryParams.append("limit", params.limit.toString());
  }

  const endpoint = `properties/${propertyId}/funnels?${queryParams.toString()}`;
  const fullUrl = EXTERNAL_API_URL + endpoint;

  const response = await fetch(fullUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw createFunnelApiError("Funnel", response, errorData);
  }

  return await response.json();
};

/**
 * Helper function to format funnel step for API
 */
const createFunnelStep = (config: {
  name: string;
  propertyId?: string;
  conditions: Array<{
    type: "page_view" | "event";
    path?: string;
    action?: "click" | "form_submission" | "revenue";
    target?: string;
    secondary?: string;
    filters?: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_content?: string;
      utm_id?: string;
      utm_term?: string;
      device?: string;
      os?: string;
      browser?: string;
      referrer?: string;
      region?: string;
      country?: string;
    };
  }>;
  minDelay?: number;
  maxDelay?: number;
}) => {
  return {
    name: config.name,
    ...(config.propertyId && { property_id: config.propertyId }),
    conditions: config.conditions.map((cond) => ({
      type: cond.type,
      ...(cond.path && { path: cond.path }),
      ...(cond.action && { action: cond.action }),
      ...(cond.target && { target: cond.target }),
      ...(cond.secondary && { secondary: cond.secondary }),
      ...(cond.filters?.utm_source && { utm_source: cond.filters.utm_source }),
      ...(cond.filters?.utm_medium && { utm_medium: cond.filters.utm_medium }),
      ...(cond.filters?.utm_campaign && {
        utm_campaign: cond.filters.utm_campaign,
      }),
      ...(cond.filters?.device && { device: cond.filters.device }),
      ...(cond.filters?.os && { os: cond.filters.os }),
      ...(cond.filters?.browser && { browser: cond.filters.browser }),
      ...(cond.filters?.referrer && { referrer: cond.filters.referrer }),
      ...(cond.filters?.region && { region: cond.filters.region }),
      ...(cond.filters?.utm_content && { utm_content: cond.filters.utm_content }),
      ...(cond.filters?.utm_id && { utm_id: cond.filters.utm_id }),
      ...(cond.filters?.utm_term && { utm_term: cond.filters.utm_term }),
      ...(cond.filters?.country && { country: cond.filters.country }),
    })),
    ...(config.minDelay !== undefined && { min_delay: config.minDelay }),
    ...(config.maxDelay !== undefined && { max_delay: config.maxDelay }),
  };
};

/**
 * Validate funnel configuration before sending to API
 */
export const validateFunnelConfig = (params: FunnelRequestParams): string[] => {
  const errors: string[] = [];

  if (!params.start || !params.end) {
    errors.push("Start and end dates are required");
  }

  if (!params.steps || params.steps.length < 2) {
    errors.push("At least 2 funnel steps are required");
  }

  if (params.steps && params.steps.length > 20) {
    errors.push("Maximum 20 funnel steps allowed");
  }

  params.steps?.forEach((step, index) => {
    if (!step.name || step.name.trim() === "") {
      errors.push(`Step ${index + 1}: Name is required`);
    }

    if (!step.conditions || step.conditions.length === 0) {
      errors.push(`Step ${index + 1}: At least one condition is required`);
    }

    if (step.conditions && step.conditions.length > 10) {
      errors.push(`Step ${index + 1}: Maximum 10 conditions per step`);
    }

    step.conditions?.forEach((condition, condIndex) => {
      if (condition.type === "page_view" && !condition.path) {
        errors.push(
          `Step ${index + 1}, Condition ${
            condIndex + 1
          }: Path is required for page_view`
        );
      }

      if (condition.type === "event" && !condition.action) {
        errors.push(
          `Step ${index + 1}, Condition ${
            condIndex + 1
          }: Action is required for event`
        );
      }
    });

    if (
      step.min_delay !== undefined &&
      step.max_delay !== undefined &&
      step.min_delay > step.max_delay
    ) {
      errors.push(
        `Step ${index + 1}: min_delay cannot be greater than max_delay`
      );
    }
  });

  return errors;
};

/**
 * Get funnel step suggestions (available pages and events)
 */
export const getFunnelSuggestions = async (
  propertyId: string,
  apiKey: string,
  options?: {
    start?: string;
    end?: string;
    search?: string;
    timezone?: string;
    granularity?: string;
  }
): Promise<FunnelSuggestionsResponse> => {
  const queryParams = new URLSearchParams();

  if (options?.start) {
    queryParams.append("start", options.start);
  }
  if (options?.end) {
    queryParams.append("end", options.end);
  }
  if (options?.timezone) {
    queryParams.append("timezone", options.timezone);
  }
  if (options?.granularity) {
    queryParams.append("granularity", options.granularity);
  }
  if (options?.search) {
    queryParams.append("search", options.search);
  }

  const endpoint = `properties/${propertyId}/funnels/suggestions${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(EXTERNAL_API_URL + endpoint, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${apiKey}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw createFunnelApiError("Suggestions", response, errorData);
  }

  return await response.json();
};
